import { getUserOrgIds } from 'Data/context/Organizations';
import { user_roles } from 'Data/Enum';
import { LineItemData } from 'Data/LineItemData';
import RequestData from 'Data/RequestData';
import ServiceContractTransactionsData from 'Data/ServiceContractTransactionsData';
import ShippingData from 'Data/ShippingData';
import TransactionDocumentData from 'Data/TransactionDocumentData';
import { REQUEST_STATUS_KEYS } from 'Pages/Requests/Enum';
import {
  LINE_ITEM_STATUS,
  TRANSACTION_CATEGORY,
  TRANSACTION_TYPE,
} from 'Pages/Transactions/Enum';
import {
  canUserViewAllCores,
  getGrandTotalForTransaction,
  isUserAuthorized,
} from 'Util';
import { formatDate, getDateRangeForYear } from 'Util/dateUtil';
import AppData from './AppData';
import GenericTableData from './GenericTableData';

const transactionInclude =
  'transaction_notes,line_items,shipping,documents,$charge_allocations.charge_accounts,$charge_allocations.expense_codes,$requests.documents,service_contracts';

// Transaction Module Event Bus. Handles all interactions with Transactions.
let TransactionData = GenericTableData.getTableDataBus('TRANS', 'transactions');

TransactionData.getTransactionListFunction = (fiscalYear) => {
  return (opts) => {
    let where = {
      ...(opts?.where && { ...opts.where }),
    };
    if (fiscalYear?.length) {
      where['*date_purchased'] = getDateRangeForYear(fiscalYear);
    }
    if (
      !canUserViewAllCores() &&
      !Object.hasOwn(where, '*parent_organization_id')
    ) {
      where['*parent_organization_id'] = getUserOrgIds();
    }

    let options = {
      ...opts,
      where: where,
      _include: 'line_items,$charge_allocations.expense_codes,',
    };
    return TransactionData.getRecordList(options, true);
  };
};

let exportCols = [
  { key: 'id', header: 'ID' },
  { key: 'reference_number', header: 'Ref #' },
  { key: 'date_purchased', header: 'Date' },
  { key: 'organization', header: 'Core Facility' },
  { key: 'requester', header: 'Requester' },
  { key: 'purchaser', header: 'Purchaser' },
  { key: 'vendor', header: 'Vendor' },
  { key: 'notes', header: 'Description' },
  { key: 'display_amount', header: 'Amount' },
  {
    key: 'category',
    header: 'Category',
    labelMap: Object.entries(TRANSACTION_CATEGORY).reduce(
      (mapObject, [key, value]) => {
        mapObject[key] = value;
        return mapObject;
      },
      {},
    ),
  },
  {
    key: 'transaction_type',
    header: 'Type',
    labelMap: Object.entries(TRANSACTION_TYPE).reduce(
      (mapObject, [key, value]) => {
        mapObject[key] = value;
        return mapObject;
      },
      {},
    ),
  },
  { key: 'associatedResources', header: 'Resources' },
];

TransactionData.getExportTransactionFunction = (fiscalYear) => {
  return (opts) => {
    let where = {
      ...(opts?.where && { ...opts.where }),
    };
    if (fiscalYear?.length) {
      where['*date_purchased'] = getDateRangeForYear(fiscalYear);
    }
    if (
      !canUserViewAllCores() &&
      !Object.hasOwn(where, '*parent_organization_id')
    ) {
      where['*parent_organization_id'] = getUserOrgIds();
    }

    let columns = exportCols;

    let options = {
      ...opts,
      where: where,
      _include: 'line_items,charge_allocations',
      columns,
    };
    return TransactionData.exportRecordList(options);
  };
};

TransactionData.getTransaction = function (id) {
  return TransactionData.getRecord(
    id,
    {
      _include: transactionInclude,
    },
    true,
  );
};

TransactionData.createTransactionFromRequest = function ({
  request,
  lineItems,
  shippingItem,
}) {
  let lines =
    lineItems ||
    request.line_items.filter((line) => !line.assigned_transaction_id) ||
    [];

  let shipping = shippingItem || (lines?.length && lines[0].shipping);

  let newTransaction = {
    vendor_id: request.vendor_id,
    parent_organization_id: request.parent_organization_id,
    originating_request_id: request.id,
    purchased_by: request.processing_user_id || AppData.userID(),
    created_by: AppData.userID(),
    category: 'EXPENSE',
    transaction_type: 'PCARD',
    requested_by: request.requesting_user_id,
    notes: request.notes,
    tags: request.tags,
    purchase_total:
      getGrandTotalForTransaction(
        lines,
        request.shipping_amount || shipping?.amount,
      ) * -1,
    ...(!shipping?.id && {
      shippings: [shipping],
      _include: 'shipping',
    }),
  };

  return TransactionData.createRecord(
    newTransaction,
    'TRANSACTION_CREATED_FROM_REQUEST',
  ).then((newTransaction) => {
    RequestData.updateRecord(
      {
        id: request.id,
        status: REQUEST_STATUS_KEYS.TRANSACTION_CREATED,
        date_processed: new Date(),
        processing_user_id: newTransaction.purchased_by,
        parent_organization_id: request.parent_organization_id,
      },
      'REQUEST_STATUS_UPDATED',
    );

    let lineItemsToUpdate = lines.map((line) => {
      return {
        id: line.id,
        source_request_id: request.id,
        assigned_transaction_id: newTransaction.id,
        status: LINE_ITEM_STATUS.PENDING.key,
        ...(shipping?.id && { shipping_id: shipping.id }),
        ...(newTransaction.shippings?.length &&
          newTransaction.shippings[0].id && {
            shipping_id: newTransaction.shippings[0].id,
          }),
      };
    });

    lineItemsToUpdate?.length &&
      LineItemData.updateOrCreateRecords(
        lineItemsToUpdate,
        'LINE_ITEMS_UPDATED_WITH_TRANS_ID',
      );

    shipping?.id &&
      ShippingData.updateRecord({
        id: shipping.id,
        parent_transaction_id: newTransaction.id,
      });
  });
};

const getTransactionDetails = (
  {
    vendor_id,
    parent_organization_id,
    purchased_by,
    purchase_total,
    notes,
    category,
    transaction_type,
    reference_number,
    documents,
    line_items,
    shippings,
  },
  includeLines = false,
) => {
  return {
    vendor_id,
    parent_organization_id,
    purchase_total,
    notes,
    category,
    transaction_type,
    reference_number,
    documents,
    ...(shippings?.length && {
      shippings: shippings.map(
        ({ delivery_address_id, shipping_speed, amount }) => {
          return {
            delivery_address_id,
            shipping_speed,
            amount,
          };
        },
      ),
    }),
    ...(includeLines && {
      line_items: line_items.map(LineItemData.getSimpleLineItem),
    }),
    ...(isUserAuthorized({
      roles: [user_roles.BUYER, user_roles.TRANSACTOR],
    })
      ? { purchased_by: AppData.userID() }
      : { purchased_by }),
    date_purchased: formatDate(new Date()),
  };
};

TransactionData.getTransactionDetails = getTransactionDetails;

TransactionData.createNewTransaction = function (
  transactionObject,
  deliveryAddressId,
) {
  let lines =
    transactionObject.line_items?.length > 0
      ? transactionObject.line_items.map(LineItemData.getSimpleLineItem)
      : [];
  let docs =
    transactionObject.documents?.map(({ id }) => {
      return { id };
    }) || [];
  let scs =
    transactionObject.service_contracts?.map(({ id }) => {
      return { id };
    }) || [];

  let includeArray = [
    ...(deliveryAddressId ? ['shipping'] : []),
    ...(lines.length ? ['line_items'] : []),
  ];

  return TransactionData.createRecord(
    {
      ...transactionObject,
      created_by: AppData.userID(),
      ...(deliveryAddressId && {
        shippings: [{ delivery_address_id: deliveryAddressId }],
      }),
      ...(lines?.length && {
        line_items: lines,
      }),

      ...(includeArray?.length && {
        _include: includeArray.join(','),
      }),
    },
    'TRANSACTION_CREATED',
  ).then(async (newTransaction) => {
    for (const sc of scs) {
      await ServiceContractTransactionsData.createRecord({
        service_contract_id: sc.id,
        transaction_id: newTransaction.id,
      });
    }
    for (const doc of docs) {
      await TransactionDocumentData.createRecord({
        document_id: doc.id,
        transaction_id: newTransaction.id,
      });
    }
  });
};

TransactionData.deleteTransaction = (transaction) => {
  return TransactionData.deleteRecords([transaction.id]).then(() => {
    if (transaction.originating_request_id) {
      RequestData.updateStatus(
        {
          id: transaction.originating_request_id,
          parent_organization_id: transaction.parent_organization_id,
          date_approved: 'placeholder so it doesnt get updated',
        },
        REQUEST_STATUS_KEYS.APPROVED,
      );
    }
  });
};

TransactionData.markAsPurchased = (transaction, lineItems) => {
  let lines = lineItems || transaction.line_items;
  let updatedFields = {
    id: transaction.id,
    date_purchased: new Date(),
    purchased_by: AppData.userID(),
    parent_organization_id: transaction.parent_organization_id,
  };
  return TransactionData.updateRecord(
    updatedFields,
    'TRANSACTION_MARKED_AS_PURCHASED',
  ).then((updatedTransaction) => {
    let lineItemsToUpdate = lines
      ?.filter(
        (line) =>
          line.status !== LINE_ITEM_STATUS.RECEIVED.key &&
          line.assigned_transaction_id === updatedTransaction.id,
      )
      .map((line) => {
        return {
          id: line.id,
          status: LINE_ITEM_STATUS.PURCHASED.key,
        };
      });
    lineItemsToUpdate?.length &&
      LineItemData.updateOrCreateRecords(
        lineItemsToUpdate,
        'LINE_ITEMS_MARKED_AS_PURCHASED',
      );
  });
};

TransactionData.updatePurchaseTotal = (
  transaction,
  lineItems,
  shippingAmount,
) => {
  if (transaction?.id && lineItems?.length) {
    let grandTotal =
      getGrandTotalForTransaction(lineItems, shippingAmount) *
      (transaction.category === 'EXPENSE' ? -1 : 1);

    if (+transaction.purchase_total !== grandTotal) {
      let updatedTransaction = {
        id: transaction.id,
        parent_organization_id: transaction.parent_organization_id,
        purchase_total: grandTotal,
      };
      return TransactionData.updateRecord(
        updatedTransaction,
        'PURCHASE_TOTAL_UPDATED',
      );
    }
  }
};

const GetTransactionDataBus = () => {
  let newTransactionBus = GenericTableData.getTableDataBus(
    'TRANS',
    'transactions',
  );
  newTransactionBus.getTransaction = function (id) {
    return newTransactionBus.getRecord(
      id,
      {
        _include: transactionInclude,
      },
      true,
    );
  };

  newTransactionBus.updateTransaction = (transaction) => {
    let scs =
      transaction.service_contracts?.map(({ id }) => {
        return { id };
      }) || [];

    if (
      scs?.length &&
      isUserAuthorized({
        roles: [user_roles.SC_ADMIN],
      })
    ) {
      return ServiceContractTransactionsData.deleteServiceContractTransaction(
        transaction.id,
      ).then(() => {
        newTransactionBus
          .updateRecord(transaction)
          .then(async (newTransaction) => {
            for (const sc of scs) {
              await ServiceContractTransactionsData.createRecord({
                service_contract_id: sc.id,
                transaction_id: newTransaction.id,
              });
            }
          });
      });
    } else {
      return newTransactionBus
        .updateRecord(transaction)
        .then(async (newTransaction) => {
          for (const sc of scs) {
            await ServiceContractTransactionsData.createRecord({
              service_contract_id: sc.id,
              transaction_id: newTransaction.id,
            });
          }
        });
    }
  };

  return newTransactionBus;
};

export { TransactionData, GetTransactionDataBus };
